import { useEffect, useMemo, useState } from "react";
import { BsLinkedin } from "react-icons/bs";
import { Check, Loader2, Send, Settings2, X } from "lucide-react";
import { LinkedinTemplate, ProspectDetail, ProspectState, getProspect } from "../../interfaces/linkedinProspecting";
import { Button } from "../../components/ui/Button";
import { ProspectMeta, SectionTitle } from "./parts";
import { DEFAULT_OFFER, INVITE_NOTE_LIMIT, OFFER_TOKEN, angleFor, relativeFr } from "./vocabulary";
import { Me, fillVariables, variableValues } from "./variables";
import { VariableTextarea } from "./VariableTextarea";

/**
 * The prospect's sheet, opened from any row: the invitation note to edit, the activity, and the
 * "client" switch. Everything else the old side panel carried is gone on purpose — the list rows
 * already say who, how big and where.
 */
export default function InviteDialog({
    personId, me, templates, onManage, onClose, onInvite, onSigned,
}: {
    personId: string;
    /** The rep: their offer (empty = the default sentence), name, company, phone — for the variables. */
    me: Me;
    /** The rep's LinkedIn templates, in their order. */
    templates: LinkedinTemplate[];
    /** Opens the offer / templates manager, on the part named. */
    onManage: (focus: "offer" | "templates") => void;
    onClose: () => void;
    /** Records the invitation, copies the note, opens the profile. */
    onInvite: (personId: string, note: string, linkedinUrl: string) => Promise<boolean>;
    onSigned: (personId: string, signed: boolean) => Promise<ProspectState | null>;
}) {
    const [detail, setDetail] = useState<ProspectDetail | null>(null);
    const [failed, setFailed] = useState(false);
    const [templateId, setTemplateId] = useState<string | null>(null);
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getProspect(personId)
            .then((d) => !cancelled && setDetail(d))
            .catch(() => !cancelled && setFailed(true));
        return () => {
            cancelled = true;
        };
    }, [personId]);

    // First draft, once both the prospect and the templates are in: the template written for the
    // prospect's situation (their signal, else their portfolio), else the first one — variables
    // kept, shown coloured, filled when sent. A saved note (already invited) wins. Later edits are
    // the rep's — templates changing don't overwrite them.
    useEffect(() => {
        if (!detail || templateId !== null || message) return;
        if (detail.note) {
            setMessage(detail.note);
            return;
        }
        const angle = angleFor(detail.signals[0], detail.portfolio.sites);
        const pick = templates.find((t) => t.linkedinAngle === angle) || templates[0];
        if (!pick) return;
        setTemplateId(pick._id);
        setMessage(pick.text);
    }, [detail, templates, templateId, message]);

    const applyTemplate = (t: LinkedinTemplate) => {
        if (!detail) return;
        setTemplateId(t._id);
        setMessage(t.text);
    };

    useEffect(() => {
        const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", esc);
        return () => document.removeEventListener("keydown", esc);
    }, [onClose]);

    // What LinkedIn will receive — the limit and the counter apply to this, not to the token.
    const values = useMemo(
        () => (detail ? variableValues({ ...detail, signals: detail.signals }, me) : {}),
        [detail, me]
    );
    const resolved = fillVariables(message, values);
    const over = resolved.length > INVITE_NOTE_LIMIT;
    const usesOffer = message.includes(OFFER_TOKEN);
    const customOffer = !!me.offer.trim() && me.offer.trim() !== DEFAULT_OFFER;
    const invited = detail?.state.invite?.status === "sent";


    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4" onMouseDown={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={detail ? `Inviter ${detail.name}` : "Prospect"}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex max-h-screen w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-surface shadow-float sm:max-h-[90vh] sm:rounded-2xl"
            >
                {!detail ? (
                    <div className="flex justify-center p-12">
                        {failed ? (
                            <p className="font-sans text-sm text-ink-muted">Impossible de charger ce prospect.</p>
                        ) : (
                            <Loader2 className="h-5 w-5 animate-spin text-ink-faint" />
                        )}
                    </div>
                ) : (
                    <>
                        {/* Identity */}
                        <div className="flex items-start gap-3 px-5 pb-4 pt-5">
                            <div className="min-w-0 flex-1 space-y-1">
                                <a
                                    href={detail.linkedinUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 font-sans text-lg font-semibold text-ink hover:underline"
                                >
                                    {detail.name}
                                    <BsLinkedin className="h-4 w-4 text-info" />
                                </a>
                                <ProspectMeta age={detail.age} portfolio={detail.portfolio} external={detail.external} name={detail.name} />
                            </div>
                            <button
                                onClick={onClose}
                                className="rounded-lg p-1.5 text-ink-faint hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                aria-label="Fermer"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {/* Message — an invitation sent elsewhere has none to show */}
                            {(!invited || message) && (
                            <div className="border-t border-border px-5 py-4">
                                <div className="mb-2 flex items-center justify-between">
                                    <SectionTitle>Message d'invitation</SectionTitle>
                                    {!invited && (
                                        <button
                                            onClick={() => onManage("templates")}
                                            className="-mt-2 inline-flex items-center gap-1 rounded font-sans text-xs text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                        >
                                            <Settings2 className="h-3.5 w-3.5" />
                                            Gérer les templates
                                        </button>
                                    )}
                                </div>

                                {/* One chip per template; picking one rewrites the draft from it */}
                                {!invited && templates.length > 0 && (
                                    <div className="mb-2 flex flex-wrap gap-1">
                                        {templates.map((t) => (
                                            <button
                                                key={t._id}
                                                type="button"
                                                onClick={() => applyTemplate(t)}
                                                aria-pressed={templateId === t._id}
                                                className={`rounded-full border px-2.5 py-0.5 font-sans text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                                    templateId === t._id
                                                        ? "border-ink bg-ink text-ink-inverse"
                                                        : "border-border text-ink-muted hover:border-border-strong hover:text-ink"
                                                }`}
                                            >
                                                {t.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {!invited && templates.length === 0 && (
                                    <p className="mb-2 font-sans text-xs text-ink-faint">
                                        Aucun template.{" "}
                                        <button onClick={() => onManage("templates")} className="font-medium text-ink-muted underline underline-offset-2 hover:text-ink">
                                            En créer un
                                        </button>
                                    </p>
                                )}
                                <VariableTextarea
                                    value={message}
                                    onChange={setMessage}
                                    values={values}
                                    readOnly={invited}
                                    invalid={over}
                                />
                                <p className={`mt-1 text-right font-mono text-xs ${over ? "text-danger" : "text-ink-faint"}`}>
                                    {resolved.length} / {INVITE_NOTE_LIMIT}
                                </p>
                                {!invited && usesOffer && !customOffer && (
                                    <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-warning-subtle px-3 py-2 font-sans text-xs text-warning">
                                        <span>Définissez votre offre pour personnaliser vos messages.</span>
                                        <button
                                            onClick={() => onManage("offer")}
                                            className="rounded font-medium underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                        >
                                            Définir mon offre
                                        </button>
                                    </p>
                                )}
                            </div>
                            )}

                            {/* Activity */}
                            <div className="border-t border-border px-5 py-4">
                                <SectionTitle>Activité</SectionTitle>
                                {detail.timeline.length === 0 ? (
                                    <p className="font-sans text-sm text-ink-faint">Rien ces 4 derniers mois.</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {detail.timeline.slice(0, 8).map((item, i) => (
                                            <li key={i} className="flex items-baseline gap-3 font-sans text-sm">
                                                <span className="w-24 shrink-0 text-xs text-ink-faint">{relativeFr(item.at)}</span>
                                                <span className={item.kind === "signal" ? "text-ink" : "text-ink-muted"}>
                                                    {item.label}
                                                    {item.detail && <span className="text-ink-faint"> · {item.detail}</span>}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center gap-2 border-t border-border px-5 py-3">
                            <button
                                disabled={busy}
                                onClick={async () => {
                                    setBusy(true);
                                    const state = await onSigned(detail.personId, !detail.state.customer);
                                    if (state) setDetail({ ...detail, state });
                                    setBusy(false);
                                }}
                                className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 font-sans text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40 ${
                                    detail.state.customer ? "text-success hover:bg-success-subtle" : "text-ink-muted hover:bg-surface-sunken hover:text-ink"
                                }`}
                            >
                                {detail.state.customer && <Check className="h-4 w-4" />}
                                {detail.state.customer ? "Client" : "Marquer comme client"}
                            </button>
                            <div className="ml-auto flex gap-2">
                                {invited ? (
                                    <Button size="sm" variant="secondary" onClick={onClose}>
                                        Fermer
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        disabled={busy || over || !message.trim()}
                                        onClick={async () => {
                                            setBusy(true);
                                            const ok = await onInvite(detail.personId, resolved, detail.linkedinUrl);
                                            setBusy(false);
                                            if (ok) onClose();
                                        }}
                                    >
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                        Inviter
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

